import { useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Activity, ArrowUpRight, Hand, Plus, Search, Workflow } from 'lucide-react';
import { TRIGGER_LABELS, type AutomationRunStatus, type Trigger } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useApprovals, useAutomationRuns, useAutomations, useMe } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { StatusDot } from '../../components/Status';
import { toast } from '../../components/Toasts';
import { EmptyState } from '../../components/EmptyState';
import { elapsed, relative } from '../../lib/format';
import { BuilderPage } from './Builder';
import { RunPage } from './RunDetail';
import { queryKeys } from '../../api/queryKeys';
import { LoadMore } from '../../components/LoadMore';
import { OverflowTabs } from '../../components/OverflowTabs';
// 1.6.0 (A, AiFlowTemplates.tsx): the example AI description flows.
import { AiFlowTemplatesButton } from './AiFlowTemplates';
// 1.6.2: the lock pill of a workflow that names who can run it (RunAccess.tsx).
import { RunAccessPill } from './RunAccess';

const RUN_LABEL: Record<AutomationRunStatus, string> = {
  queued: 'Queued', waiting_resources: 'Waiting for capacity', running: 'Running', waiting_approval: 'Waiting for approval',
  retrying: 'Retrying', succeeded: 'Succeeded', failed: 'Failed', cancelled: 'Cancelled',
};
const RUN_CLASS: Record<AutomationRunStatus, string> = {
  queued: 'registered', waiting_resources: 'partial', running: 'processing', waiting_approval: 'partial', retrying: 'partial', succeeded: 'ready', failed: 'failed', cancelled: 'offline',
};
export function RunStatus({ status }: { status: AutomationRunStatus }) {
  return <span className={`status ${RUN_CLASS[status]}`} data-testid="run-status" data-status={status}><span className="dot" aria-hidden />{RUN_LABEL[status]}</span>;
}

export function Workflows() {
  return (
    <Routes>
      <Route path="edit/:id" element={<BuilderPage />} />
      <Route path="runs/:id" element={<RunPage />} />
      <Route path="*" element={<WorkflowsHome />} />
    </Routes>
  );
}

function WorkflowsHome() {
  const { pathname } = useLocation();
  // Approvals moved to Tasks (0.31.0); the tab links there.
  const approvals = useApprovals({ scope: 'mine' }).data?.items.filter((a) => a.canDecide).length ?? 0;
  const tabs = [
    { to: 'list', label: 'Workflows', icon: <Workflow size={15} /> },
    { to: 'runs', label: 'Runs', icon: <Activity size={15} /> },
  ];
  return (
    <>
      <header className="topbar">
        <h1><Workflow size={16} color="var(--accent-strong)" /> Workflows</h1>
        <OverflowTabs as="nav" className="admin-tabs" wrapClassName="admin-tabs-wrap" label="Workflow sections" activeKey={pathname} testId="wf-tabs">
          {tabs.map((t) => <NavLink key={t.to} to={`/workflows/${t.to}`} className={({ isActive }) => `admin-tab ${isActive ? 'active' : ''}`} data-testid={`wf-tab-${t.to}`}>{t.icon}{t.label}</NavLink>)}
          <Link to="/tasks?view=approvals" className="admin-tab" data-testid="wf-tab-approvals" title="Approvals are in Tasks"><Hand size={15} />{approvals ? `Approvals (${approvals})` : 'Approvals'} <ArrowUpRight size={12} /></Link>
        </OverflowTabs>
      </header>
      <Routes>
        <Route path="list" element={<WorkflowList />} />
        <Route path="runs" element={<RunList />} />
        <Route path="approvals" element={<Navigate to="/tasks?view=approvals" replace />} />
        <Route path="operations" element={<Navigate to="/monitor?tab=operations" replace />} />
        <Route path="*" element={<Navigate to="/workflows/list" replace />} />
      </Routes>
    </>
  );
}

function WorkflowList() {
  const me = useMe().data!;
  const q = useAutomations();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const canManage = me.permissions.includes('automation.manage');

  async function create() {
    try {
      const r = await api<{ id: string }>('/api/v1/automations', { method: 'POST', json: { name: name.trim() || 'Untitled workflow' } });
      await qc.invalidateQueries({ queryKey: queryKeys.automations() });
      navigate(`/workflows/edit/${r.id}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  const everything = q.data?.items ?? [];
  const [text, setText] = useState('');
  const [state, setState] = useState<'all' | 'enabled' | 'off'>('all');
  const [starts, setStarts] = useState<string[]>([]);
  // Only the trigger types some workflow uses are offered.
  const triggerTypes = [...new Set(everything.map((w) => w.triggerType).filter((t): t is string => Boolean(t)))].sort((a, b) => (TRIGGER_LABELS[a as Trigger['type']] ?? a).localeCompare(TRIGGER_LABELS[b as Trigger['type']] ?? b));
  const needle = text.trim().toLowerCase();
  const items = everything.filter((w) =>
    (!needle || w.name.toLowerCase().includes(needle)) &&
    (state === 'all' || (state === 'enabled') === w.enabled) &&
    (!starts.length || (w.triggerType != null && starts.includes(w.triggerType))));
  const filtered = Boolean(needle) || state !== 'all' || starts.length > 0;
  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h2>Automations</h2>
          <p className="lede">Each workflow runs a published version of its graph. Editing changes the draft only; publishing creates a new version and in-flight runs finish on the version they started with.</p>
        </div>
        {canManage && <AiFlowTemplatesButton />}
        {canManage && <button className="btn primary" onClick={() => setOpen(true)} data-testid="new-workflow"><Plus size={15} /> New workflow</button>}
      </div>
      {everything.length > 0 && (
        <div className="list-filters" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }} data-testid="workflow-filters">
          <div className="search" role="search" style={{ maxWidth: 300 }}>
            <Search size={15} />
            <input className="input" placeholder="Search workflows…" value={text} onChange={(e) => setText(e.target.value)} aria-label="Search workflows" data-testid="workflow-search" />
          </div>
          <div className="segmented" role="group" aria-label="State">
            {([['all', 'Any state'], ['enabled', 'Enabled'], ['off', 'Off']] as const).map(([k, label]) => (
              <button key={k} aria-pressed={state === k} onClick={() => setState(k)} data-testid={`wf-state-${k}`}>{label}</button>
            ))}
          </div>
          {triggerTypes.length > 0 && (
            <div className="chips" role="group" aria-label="Starts when" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 12, lineHeight: '24px' }}>Starts when</span>
              {triggerTypes.map((t) => (
                <button key={t} className={`chip ${starts.includes(t) ? 'active' : ''}`} aria-pressed={starts.includes(t)} data-testid={`wf-start-${t}`}
                  onClick={() => setStarts((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]))}>
                  {TRIGGER_LABELS[t as Trigger['type']] ?? t}
                </button>
              ))}
            </div>
          )}
          {filtered && <span className="muted" data-testid="workflow-count">{items.length} of {everything.length}</span>}
        </div>
      )}
      {everything.length === 0 && !q.isLoading ? (
        <EmptyState icon={<Workflow size={24} />} title="Automate the routine">
          <p>Validate metadata when files land, make delivery renditions, ask a producer to approve, copy to archive and notify another system, all as a visual flow.</p>
        </EmptyState>
      ) : items.length === 0 ? (
        <p className="muted" data-testid="workflow-none">No workflows match.</p>
      ) : (
        <table className="table" data-testid="workflow-list">
          <thead><tr><th>Name</th><th>Starts when</th><th>Version</th><th>State</th><th>Last 24 h</th><th>Updated</th></tr></thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} tabIndex={0} onClick={() => navigate(`/workflows/edit/${w.id}`)} onKeyDown={(e) => e.key === 'Enter' && navigate(`/workflows/edit/${w.id}`)}>
                <td className="wrap" style={{ fontWeight: 560 }} data-testid="workflow-row" data-name={w.name}>{w.name}{w.restricted && <> <RunAccessPill access={w.runAccess} mayRun={w.mayRun} /></>}</td>
                <td className="muted">{w.triggerType ? TRIGGER_LABELS[w.triggerType as Trigger['type']] : '—'}</td>
                <td className="mono">{w.currentVersion ? `v${w.currentVersion}` : <span className="faint">draft</span>}</td>
                <td>{w.enabled ? <StatusDot tone="ready">Enabled</StatusDot> : <StatusDot tone="offline">Off</StatusDot>}</td>
                <td className="muted">{w.runs24h} runs{w.failed24h ? <span className="warn-text"> · {w.failed24h} failed</span> : ''}{w.active ? ` · ${w.active} active` : ''}</td>
                <td className="muted">{relative(w.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && (
        <Modal title="New workflow" onClose={() => setOpen(false)} actions={<><button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button><button className="btn primary" onClick={() => void create()} data-testid="create-workflow">Create</button></>}>
          <div className="field"><label htmlFor="wf-name">Name</label><input id="wf-name" className="input" autoFocus value={name} placeholder="Archive approved news" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void create()} /></div>
        </Modal>
      )}
    </div>
  );
}

const RUN_FILTERS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' },
  { key: 'queued,waiting_resources,running,retrying,waiting_approval', label: 'Active' },
  { key: 'waiting_approval', label: 'Waiting' },
  { key: 'failed', label: 'Failed' },
  { key: 'succeeded', label: 'Succeeded' },
];

export function RunList({ definitionId, compact = false }: { definitionId?: string; compact?: boolean }) {
  const [status, setStatus] = useState('');
  const q = useAutomationRuns({ definitionId, status });
  const navigate = useNavigate();
  const items = q.data?.items ?? [];
  const body = (
    <>
      <div className="segmented" style={{ marginBottom: 12 }}>
        {RUN_FILTERS.map((f) => <button key={f.label} aria-pressed={status === f.key} onClick={() => setStatus(f.key)}>{f.label}</button>)}
      </div>
      {items.length === 0 ? <p className="muted">{q.isLoading ? 'Loading…' : 'No runs match.'}</p> : (
        <table className="table" data-testid="run-list">
          <thead><tr>{!compact && <th>Workflow</th>}<th>Status</th><th>Trigger</th><th>Asset</th><th>Started</th><th>Duration</th></tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} tabIndex={0} onClick={() => navigate(`/workflows/runs/${r.id}`)} onKeyDown={(e) => e.key === 'Enter' && navigate(`/workflows/runs/${r.id}`)}>
                {!compact && <td className="wrap" style={{ fontWeight: 560 }}>{r.definitionName} <span className="faint mono">v{r.version}</span></td>}
                <td><RunStatus status={r.status} /></td>
                <td className="muted wrap">{TRIGGER_LABELS[r.trigger.type]}{r.trigger.actor ? ` · ${r.trigger.actor}` : ''}</td>
                <td className="muted wrap">{r.trigger.assetTitle ?? '—'}</td>
                <td className="muted">{relative(r.createdAt)}</td>
                <td className="mono muted">{r.startedAt ? elapsed(r.startedAt, r.finishedAt) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <LoadMore list={q} shown={items.length} noun="runs" />
    </>
  );
  return compact ? body : <div className="page"><h2>Runs</h2><p className="lede">Every run records the version it used, what started it, each step’s attempts, output and errors.</p>{body}</div>;
}
